export interface FlacDownmix {
  type: 'flacDownmix';

  /**
   * An empty array means that we don't know yet the number of audio tracks
   * and we have no default downmix matrix to apply. In this case, the transmuxer
   * will apply a default layout when it knows how many channels there are. This
   * default layout consist of even channels going to the left and odd channels
   * going to the right.
   *
   * Otherwise, a Nx2 matrix where N is the number of audio channels
   *
   * Example: for 4 channels with channel 0 and 1 going to left, channel 2 going to right
   *          and channel 3 going to both left and right, we would have:
   *
   *          [
   *             [ 1.0, 0.0 ],
   *             [ 1.0, 0.0 ],
   *             [ 0.0, 1.0 ],
   *             [ 1.0, 1.0 ],
   *          ]
   *
   * Note that it is allowed to have less rows than the number of channels in the media.
   * In particular, this may be the case when we want to apply the webproxy downmix as the
   * default mix matrix before we know the actual number of channels. In this case, the transmuxer
   * will treat any missing channel as muted.
   */
  mixMatrix: number[][];
}


export type AudioTransform = FlacDownmix;

/**
 * The purpose of this function is to return a string that identifies
 * the given transform. Since this identity will be used to check if
 * a new transform is different from the previous one in order to decide
 * whether or not we need to evict data that we had and build a new transmuxer,
 * it is very important that this function returns the same value for 2 transforms
 * that correspond to the same actual downmix operation. This is why we ignore
 * zeroes so that the following mix matrices A and B would get the same ID since
 * apply A or B would give the same result:
 *
 * A: [
 *      [1.0, 0.0],
 *      [0.0, 1.0],
 *    ]
 *
 * B: [
 *      [1.0, 0.0],
 *      [0.0, 1.0],
 *      [0.0, 0.0],
 *      [0.0, 0.0],
 *    ]
 */
export function getAudioTransformId(t: AudioTransform): string {
  if (t.mixMatrix === null) {
    return 'downmix:null';
  }

  let left = '';
  let right = '';

  t.mixMatrix.forEach((e, index) => {
    if (e[0] !== 0.0) {
      left += `:${index}|${e[0]}`;
    }
    if (e[1] !== 0.0) {
      right += `:${index}|${e[1]}`;
    }
  });

  return `downmix:L:${left}:R:${right}`;
}


export function sameAudioTransforms(a: AudioTransform, b: AudioTransform) {
  return getAudioTransformId(a) === getAudioTransformId(b);
}
